import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import ScrollToBottom from 'react-scroll-to-bottom';
import SelectQuestion from 'sharedComponents/Quiz/components/SelectQuestion';
import InputQuestion from 'sharedComponents/Quiz/components/InputQuestion';
import SavingsQuestion from 'sharedComponents/Quiz/components/SavingsQuestion';
import AnnualIncomeQuestion from 'sharedComponents/Quiz/components/AnnualIncomeQuestion';
import SavingsMinimumQuestion from 'sharedComponents/Quiz/components/SavingsMinimumQuestion';
import MortgagePaymentQuestion from 'sharedComponents/Quiz/components/MortgagePaymentQuestion';
import MortgageBalanceQuestion from 'sharedComponents/Quiz/components/MortgageBalanceQuestion';
import IntermediateResults from 'sharedComponents/Quiz/components/IntermediateResults';
import QuizQuestionPresenter from 'presenters/QuizQuestionPresenter';
import QuizPresenter from 'presenters/QuizPresenter';
import Icon from 'sharedComponents/Icon';
import { Box, Typography, Button } from '@material-ui/core';

import useStyles from './useStyles';

const QUESTION_TYPE_MAPPER = {
  selectQuestion: SelectQuestion,
  inputQuestion: InputQuestion,
  mortgageBalanceQuestion: MortgageBalanceQuestion,
  savingsQuestion: SavingsQuestion,
  mortgagePaymentQuestion: MortgagePaymentQuestion,
  annualIncomeQuestion: AnnualIncomeQuestion,
  savingsMinimumQuestion: SavingsMinimumQuestion,
};

const DesktopQuiz = props => {
  const { t } = useTranslation();
  const classes = useStyles();
  const {
    currentQuestion,
    quizValues,
    onNext,
    onChange,
    isCurrentQuestionValid,
    setCurrentQuestionValidity,
    manuallyChangedFields,
    setManuallyChangedField,
    calculatedValues,
    answeredQuestions,
    isLastQuestion,
    onFinish,
  } = props;

  const currentQuestionType = QuizQuestionPresenter.type(currentQuestion);
  const currentQuestionLabel = QuizQuestionPresenter.label(currentQuestion);
  const CurrentQuestionComponent = QUESTION_TYPE_MAPPER[currentQuestionType];
  const { mortgagePaymentFinal, houseValue, houseValueDp } = calculatedValues;

  return (
    <Box className={classes.paper}>
      <Box className={classes.leftBar}>
        <Typography variant="h1">{t('components.Quiz.DesktopQuiz.quiz')}</Typography>
        <Box mt={4}>
          {answeredQuestions.map(question => {
            const label = QuizQuestionPresenter.label(question);
            return (
              <Box key={label} display="flex" alignItems="top" mb={1}>
                <Box width={18} height={18} mr={2}>
                  <Icon name="star" className={classes.icon} />
                </Box>
                <Typography variant="body1">{label}</Typography>
              </Box>
            );
          })}
          <Box display="flex" alignItems="top" mb={1}>
            <Box width={18} height={18} mr={2}>
              <Icon name="contourStar" className={classes.icon} />
            </Box>
            <Typography variant="body1">{currentQuestionLabel}</Typography>
          </Box>
        </Box>
      </Box>
      <Box className={classes.scrollContainer}>
        <ScrollToBottom className={classes.quizContainer} scrollViewClassName={classes.scrollView}>
          <Box mt={4}>
            {answeredQuestions.map(question => {
              const type = QuizQuestionPresenter.type(question);
              const QuestionComponent = QUESTION_TYPE_MAPPER[type];
              const label = QuizQuestionPresenter.label(question);
              return (
                <Box key={label} mr={3} className={classes.answeredQuestions}>
                  <QuestionComponent
                    question={question}
                    values={quizValues}
                    onChange={onChange}
                    setValidity={setCurrentQuestionValidity}
                    manuallyChangedFields={manuallyChangedFields}
                    setManuallyChangedField={setManuallyChangedField}
                  />
                </Box>
              );
            })}
          </Box>
          <Box mt={4} mr={3}>
            <CurrentQuestionComponent
              question={currentQuestion}
              values={quizValues}
              onChange={onChange}
              setValidity={setCurrentQuestionValidity}
              manuallyChangedFields={manuallyChangedFields}
              setManuallyChangedField={setManuallyChangedField}
            />
          </Box>
          <Box className={classes.divider} />
          <Box height={120}>
            {isLastQuestion ? (
              <Button onClick={onFinish} disabled={!isCurrentQuestionValid}>
                {t('components.Quiz.DesktopQuiz.finish')}
              </Button>
            ) : (
              <Button onClick={onNext} disabled={!isCurrentQuestionValid}>
                {t('components.Quiz.DesktopQuiz.nextQuestion')}
              </Button>
            )}
          </Box>
        </ScrollToBottom>
      </Box>
      <Box className={classes.rightBar}>
        <IntermediateResults
          mortgagePayment={mortgagePaymentFinal}
          houseValueDp={houseValueDp}
          houseValue={houseValue}
        />
      </Box>
    </Box>
  );
};

DesktopQuiz.propTypes = {
  currentQuestion: QuizQuestionPresenter.shape().isRequired,
  quizValues: QuizPresenter.shape().isRequired,
  onNext: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  isCurrentQuestionValid: PropTypes.bool.isRequired,
  setCurrentQuestionValidity: PropTypes.func.isRequired,
  manuallyChangedFields: PropTypes.shape().isRequired,
  setManuallyChangedField: PropTypes.func.isRequired,
  calculatedValues: PropTypes.shape().isRequired,
  answeredQuestions: PropTypes.arrayOf(QuizQuestionPresenter.shape()).isRequired,
  onFinish: PropTypes.func.isRequired,
  isLastQuestion: PropTypes.bool.isRequired,
};

export default DesktopQuiz;



// WEBPACK FOOTER //
// ./src/sharedComponents/Quiz/components/DesktopQuiz/DesktopQuiz.js